/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Clock, AlertTriangle, Archive, Trash2,
  Shield, RotateCcw, XCircle, CheckCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/**
 * v77.18 — Admin "Inactive accounts" dashboard.
 *
 * Tabs map 1:1 to the lifecycle stages. Row actions call the admin
 * endpoints under /api/admin/inactive/{user_id}/...:
 *   • restore (archived → active)
 *   • cancel-deletion (clear warning flags + scheduled_at)
 *   • archive (force a user into archived state)
 *   • delete (force permanent anonymisation, requires confirm flag)
 *   • protect (toggle protected_from_inactivity)
 */

const STAGES = [
  { key: "inactive_90", label: "90+ days inactive", icon: Clock },
  { key: "reengaged", label: "Re-engaged", icon: CheckCircle },
  { key: "warned", label: "Final warning sent", icon: AlertTriangle },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "scheduled_delete", label: "Scheduled for deletion", icon: Trash2 },
  { key: "deleted", label: "Deleted", icon: XCircle },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function daysAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function StagePill({ stage }) {
  const map = {
    active: { c: "bg-[#E8F4F2] text-[#116B6E]", l: "Active" },
    inactive_90: { c: "bg-[#FBEFED] text-[#8C2A22]", l: "90+ days" },
    reengaged: { c: "bg-[#FFF7E5] text-[#8A6800]", l: "Re-engaged" },
    warned: { c: "bg-[#FBEFED] text-[#8C2A22]", l: "Warned" },
    archived: { c: "bg-[#EEF1F2] text-[#5C5C5C]", l: "Archived" },
    scheduled_delete: { c: "bg-[#C0291D] text-white", l: "Pending delete" },
    deleted: { c: "bg-[#2E3A3F] text-white", l: "Deleted" },
  };
  const v = map[stage] || map.active;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-medium ${v.c}`}>
      {v.l}
    </span>
  );
}

export default function AdminInactivePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inactive_90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bust, setBust] = useState(0);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/inactive?stage=${tab}&limit=200`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ users: [], total: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, tab, bust]);

  if (!user?.is_admin) {
    return (
      <div className="p-8 text-center text-[#5C5C5C]">
        Admin access required.
      </div>
    );
  }

  const act = async (uid, action, label, body) => {
    setBusyId(uid);
    try {
      await api.post(`/admin/inactive/${uid}/${action}`, body || {});
      toast.success(label);
      setBust((n) => n + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusyId(null);
      setConfirmingDelete(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-inactive-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/app/admin")}
          className="text-[#5C5C5C] hover:text-[#1F9FA4] inline-flex items-center gap-1.5 text-sm"
          data-testid="admin-inactive-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admin
        </button>
      </div>

      <div>
        <h1 className="font-heading text-3xl text-[#2E3A3F]">Inactive accounts</h1>
        <p className="text-sm text-[#6F828A] mt-1.5">
          Free-tier users get 90 days → re-engagement email → 7 days → final warning → 7 days → archive → 30 days → permanent anonymisation. Paid, complimentary, admin and protected accounts are exempt.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#D9E1E3] flex flex-wrap gap-x-6 gap-y-2" data-testid="admin-inactive-tabs">
        {STAGES.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`admin-inactive-tab-${key}`}
            className={`pb-2 -mb-px text-sm font-medium inline-flex items-center gap-1.5 border-b-2 transition-colors ${
              tab === key
                ? "border-[#1F9FA4] text-[#116B6E]"
                : "border-transparent text-[#6F828A] hover:text-[#2E3A3F]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[#6F828A] inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : !data?.users?.length ? (
          <div className="p-12 text-center text-[#6F828A]" data-testid="admin-inactive-empty">
            No users in this stage. Nice and tidy.
          </div>
        ) : (
          <ul className="divide-y divide-[#EEF3F4]" data-testid="admin-inactive-list">
            {data.users.map((u) => {
              const inactive_days = daysAgo(u.last_activity_at);
              return (
                <li key={u.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-inactive-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#2E3A3F] truncate">{u.name || "—"}</span>
                      <StagePill stage={u.stage} />
                      {u.protected_from_inactivity && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-sm bg-[#FFF7E5] text-[#8A6800]">
                          <Shield className="w-3 h-3" />
                          Protected
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6F828A] mt-0.5 font-mono-data truncate">{u.email}</div>
                    <div className="text-xs text-[#8A8A8A] mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span>
                        Last activity:{" "}
                        <span className="text-[#5C5C5C]">{fmtDate(u.last_activity_at)}{inactive_days != null ? ` (${inactive_days}d ago)` : ""}</span>
                      </span>
                      {u.archived_at && (
                        <span>Archived: <span className="text-[#5C5C5C]">{fmtDate(u.archived_at)}</span></span>
                      )}
                      {u.deletion_scheduled_at && (
                        <span>Scheduled delete: <span className="text-[#C0291D]">{fmtDate(u.deletion_scheduled_at)}</span></span>
                      )}
                      <span>Plan: <span className="text-[#5C5C5C]">{u.plan || "—"}</span></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {u.account_status === "archived" && (
                      <Button
                        size="sm" variant="outline"
                        disabled={busyId === u.id}
                        onClick={() => act(u.id, "restore", "Account restored")}
                        data-testid={`admin-inactive-restore-${u.id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Restore
                      </Button>
                    )}
                    {u.deletion_scheduled_at && u.account_status !== "archived" && (
                      <Button
                        size="sm" variant="outline"
                        disabled={busyId === u.id}
                        onClick={() => act(u.id, "cancel-deletion", "Deletion cancelled")}
                        data-testid={`admin-inactive-cancel-${u.id}`}
                      >
                        Cancel deletion
                      </Button>
                    )}
                    {u.account_status === "active" && u.stage !== "deleted" && (
                      <Button
                        size="sm" variant="outline"
                        disabled={busyId === u.id}
                        onClick={() => act(u.id, "archive", "Account archived")}
                        data-testid={`admin-inactive-archive-${u.id}`}
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" />
                        Archive
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      disabled={busyId === u.id}
                      onClick={() => act(u.id, "protect", u.protected_from_inactivity ? "Protection removed" : "Excluded from inactivity flow", { protected: !u.protected_from_inactivity })}
                      data-testid={`admin-inactive-protect-${u.id}`}
                    >
                      <Shield className="w-3.5 h-3.5 mr-1" />
                      {u.protected_from_inactivity ? "Unprotect" : "Exclude"}
                    </Button>
                    {u.stage !== "deleted" && (
                      confirmingDelete === u.id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-xs text-[#C0291D] font-medium">Anonymise permanently?</span>
                          <Button
                            size="sm"
                            disabled={busyId === u.id}
                            onClick={() => act(u.id, "delete", "User anonymised", { confirm: true })}
                            className="bg-[#C0291D] hover:bg-[#9A1F14] text-white"
                            data-testid={`admin-inactive-delete-confirm-${u.id}`}
                          >
                            Yes, delete
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setConfirmingDelete(null)}
                          >
                            Cancel
                          </Button>
                        </span>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setConfirmingDelete(u.id)}
                          className="text-[#C0291D] hover:bg-[#FBEFED]"
                          data-testid={`admin-inactive-delete-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Delete
                        </Button>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-xs text-[#8A8A8A] text-center">
        Showing {data?.users?.length ?? 0} of {data?.total ?? 0}.
      </p>
    </div>
  );
}
